import './styles/app.css';
import Alpine from 'alpinejs';
import { Chart, DoughnutController, ArcElement, Tooltip, Legend } from 'chart.js';
import { api, getToken, setToken } from './lib/api.js';
import { formatMoney, parseMoneyVn, formatDateVi, todayIso } from './lib/money.js';
import { detectPlatform } from './platform/index.js';
import uiHtml from './ui.html?raw';

Chart.register(DoughnutController, ArcElement, Tooltip, Legend);

const BASE = '/dashboard';

function pathFromLocation() {
  let path = window.location.pathname;
  if (path.startsWith(BASE)) {
    path = path.slice(BASE.length) || '/';
  }
  if (!path.startsWith('/')) path = `/${path}`;
  return path.replace(/\/+$/, '') || '/';
}

function blankForm() {
  return {
    amount: '',
    type: 'expense',
    description: '',
    category_id: '',
    transaction_date: todayIso(),
  };
}

function createApp() {
  const platform = detectPlatform();

  return {
    platform,
    route: '/',
    user: null,
    booting: true,
    toast: null,
    toastTimer: null,
    loginTab: 'email',
    loginEmail: '',
    loginPassword: '',
    loginCode: '',
    loginLoading: false,
    loginError: '',
    period: 'month',
    loading: false,
    stats: null,
    recent: [],
    txItems: [],
    txTotal: 0,
    txOffset: 0,
    txType: '',
    txPeriod: '',
    txQuery: '',
    sheetOpen: false,
    editingId: null,
    form: blankForm(),
    formError: '',
    formSaving: false,
    confirmDeleteId: null,
    catType: 'expense',
    categories: [],
    catFormOpen: false,
    catForm: { name: '', parent_id: null },
    catError: '',
    reportStats: null,
    chart: null,
    flatCategories: [],
    formatMoney,
    formatDateVi,

    get isMini() {
      return this.platform.id === 'telegram' || this.route.startsWith('/app');
    },

    get page() {
      const r = this.route;
      if (r === '/login') return 'login';
      if (r === '/' || r === '/app') return 'dashboard';
      if (r.startsWith('/transactions')) return 'transactions';
      if (r === '/categories') return 'categories';
      if (r === '/reports') return 'reports';
      if (r === '/settings' || r === '/menu') return r === '/menu' ? 'menu' : 'settings';
      return 'dashboard';
    },

    async init() {
      platform.theme.applyFromHost();
      platform.chrome.ready();

      window.addEventListener('popstate', () => {
        this.route = pathFromLocation();
        this.onRoute();
      });

      this.route = pathFromLocation();

      if (getToken()) {
        try {
          this.user = await api('/user');
        } catch {
          setToken(null);
          this.user = null;
        }
      }

      if (platform.id === 'telegram' && platform.initData && !getToken()) {
        try {
          const data = await api('/auth/telegram-webapp', {
            method: 'POST',
            body: { init_data: platform.initData, device_name: 'mini_app' },
          });
          setToken(data.token);
          this.user = data.user;
        } catch (e) {
          this.showToast(e.message || 'Không xác thực được Telegram', 'danger');
        }
      }

      this.booting = false;

      if (!this.user && this.page !== 'login') {
        this.navigate('/login', true);
      } else if (this.user && this.page === 'login') {
        this.navigate('/', true);
      } else {
        await this.onRoute();
      }
    },

    navigate(path, replace = false) {
      const clean = path.replace(/\/+$/, '') || '/';
      const url = `${BASE}${clean === '/' ? '/' : clean}`;
      if (replace) window.history.replaceState({}, '', url);
      else window.history.pushState({}, '', url);
      this.route = clean;
      this.onRoute();
    },

    async onRoute() {
      if (!this.user) return;
      this.sheetOpen = false;
      this.confirmDeleteId = null;
      this.destroyChart();

      if (this.page === 'dashboard') await this.loadDashboard();
      else if (this.page === 'transactions') {
        if (this.route === '/transactions/new') {
          this.openSheet();
          await this.refreshFlatCategories();
          this.navigate('/transactions', true);
        } else if (/^\/transactions\/\d+$/.test(this.route)) {
          const id = Number(this.route.split('/').pop());
          await this.loadTransactions(true);
          await this.openEdit(id);
          await this.refreshFlatCategories();
        } else {
          await this.loadTransactions(true);
        }
      } else if (this.page === 'categories') await this.loadCategories();
      else if (this.page === 'reports') await this.loadReports();
    },

    showToast(message, tone = 'success') {
      this.toast = { message, tone };
      clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => {
        this.toast = null;
      }, 3000);
    },

    async submitLogin() {
      this.loginLoading = true;
      this.loginError = '';
      try {
        let data;
        if (this.loginTab === 'email') {
          data = await api('/login', {
            method: 'POST',
            body: {
              email: this.loginEmail,
              password: this.loginPassword,
              device_name: 'web',
            },
          });
        } else {
          data = await api('/auth/telegram-code', {
            method: 'POST',
            body: { code: this.loginCode, device_name: 'web' },
          });
        }
        setToken(data.token);
        this.user = data.user;
        this.navigate('/');
      } catch (e) {
        this.loginError =
          e.data?.errors?.email?.[0] ||
          e.data?.errors?.code?.[0] ||
          e.message ||
          'Đăng nhập thất bại';
      } finally {
        this.loginLoading = false;
      }
    },

    async logout() {
      try {
        await api('/logout', { method: 'POST' });
      } catch {
        /* ignore */
      }
      setToken(null);
      this.user = null;
      this.navigate('/login', true);
    },

    async setPeriod(period) {
      this.period = period;
      if (this.page === 'dashboard') await this.loadDashboard();
      if (this.page === 'reports') await this.loadReports();
    },

    async loadDashboard() {
      this.loading = true;
      try {
        const [stats, list] = await Promise.all([
          api(`/transactions/stats?period=${this.period}`),
          api('/transactions?limit=5'),
        ]);
        this.stats = stats;
        this.recent = list.items || [];
      } catch (e) {
        if (e.status === 401) return this.logout();
        this.showToast(e.message || 'Không tải được dashboard', 'danger');
      } finally {
        this.loading = false;
      }
    },

    expenseCategories() {
      if (!this.stats?.by_category) return [];
      return this.stats.by_category.filter((c) => c.type === 'expense').slice(0, 4);
    },

    categoryShare(total) {
      const expense = Number(this.stats?.expense) || 0;
      if (!expense) return 0;
      return Math.round((Number(total) / expense) * 100);
    },

    async loadTransactions(reset = false) {
      if (reset) {
        this.txOffset = 0;
        this.txItems = [];
      }
      this.loading = true;
      try {
        const params = new URLSearchParams({
          limit: '20',
          offset: String(this.txOffset),
        });
        if (this.txType) params.set('type', this.txType);
        if (this.txPeriod) params.set('period', this.txPeriod);
        if (this.txQuery.trim()) params.set('q', this.txQuery.trim());

        const data = await api(`/transactions?${params}`);
        this.txTotal = data.total;
        this.txItems = reset ? data.items : [...this.txItems, ...data.items];
      } catch (e) {
        if (e.status === 401) return this.logout();
        this.showToast(e.message || 'Không tải giao dịch', 'danger');
      } finally {
        this.loading = false;
      }
    },

    async loadMoreTx() {
      this.txOffset = this.txItems.length;
      await this.loadTransactions(false);
    },

    openSheet(tx = null) {
      this.formError = '';
      if (tx) {
        this.editingId = tx.id;
        this.form = {
          amount: String(Math.round(Number(tx.amount))),
          type: tx.type,
          description: tx.description || '',
          category_id: tx.category_id || '',
          transaction_date: tx.date,
        };
      } else {
        this.editingId = null;
        this.form = blankForm();
      }
      this.sheetOpen = true;
      requestAnimationFrame(() => document.getElementById('amount-field')?.focus());
    },

    async openEdit(id) {
      try {
        const tx = await api(`/transactions/${id}`);
        this.openSheet(tx);
      } catch (e) {
        this.showToast(e.message || 'Không tìm thấy giao dịch', 'danger');
      }
    },

    closeSheet() {
      this.sheetOpen = false;
      this.editingId = null;
    },

    async saveTransaction() {
      this.formSaving = true;
      this.formError = '';
      const amount = parseMoneyVn(this.form.amount);
      if (!amount) {
        this.formError = 'Nhập số tiền hợp lệ';
        this.formSaving = false;
        return;
      }

      const body = {
        type: this.form.type,
        amount,
        description: this.form.description || null,
        transaction_date: this.form.transaction_date || todayIso(),
        source: this.platform.id === 'telegram' ? 'mini_app' : 'web',
      };
      if (this.form.category_id) {
        body.category_id = Number(this.form.category_id);
      }

      try {
        if (this.editingId) {
          await api(`/transactions/${this.editingId}`, { method: 'PATCH', body });
          this.showToast('Đã cập nhật giao dịch');
        } else {
          await api('/transactions', { method: 'POST', body });
          this.showToast('Đã thêm giao dịch');
          this.platform.chrome.haptic?.('success');
        }
        this.closeSheet();
        if (this.page === 'dashboard') await this.loadDashboard();
        else await this.loadTransactions(true);
      } catch (e) {
        this.formError = e.message || 'Lưu thất bại';
      } finally {
        this.formSaving = false;
      }
    },

    async deleteTransaction(id) {
      try {
        await api(`/transactions/${id}`, { method: 'DELETE' });
        this.confirmDeleteId = null;
        this.closeSheet();
        this.showToast('Đã xóa giao dịch');
        await this.loadTransactions(true);
      } catch (e) {
        this.showToast(e.message || 'Xóa thất bại', 'danger');
      }
    },

    async loadCategories() {
      this.loading = true;
      try {
        const data = await api(`/categories?type=${this.catType}&tree=1`);
        this.categories = data.items || [];
      } catch (e) {
        if (e.status === 401) return this.logout();
        this.showToast(e.message || 'Không tải danh mục', 'danger');
      } finally {
        this.loading = false;
      }
    },

    openCatForm(parentId = null) {
      this.catError = '';
      this.catForm = { name: '', parent_id: parentId };
      this.catFormOpen = true;
    },

    async saveCategory() {
      if (!this.catForm.name.trim()) {
        this.catError = 'Nhập tên danh mục';
        return;
      }
      const body = {
        name: this.catForm.name.trim(),
        type: this.catType,
      };
      if (this.catForm.parent_id) body.parent_id = this.catForm.parent_id;

      try {
        await api('/categories', { method: 'POST', body });
        this.catFormOpen = false;
        this.showToast('Đã thêm danh mục');
        await this.loadCategories();
      } catch (e) {
        this.catError = e.message || 'Không thêm được';
      }
    },

    async deleteCategory(id) {
      try {
        await api(`/categories/${id}`, { method: 'DELETE' });
        this.showToast('Đã xóa danh mục');
        await this.loadCategories();
      } catch (e) {
        this.showToast(e.message || 'Không xóa được', 'danger');
      }
    },

    async loadReports() {
      this.loading = true;
      try {
        this.reportStats = await api(`/transactions/stats?period=${this.period}`);
        requestAnimationFrame(() => this.renderChart());
      } catch (e) {
        if (e.status === 401) return this.logout();
        this.showToast(e.message || 'Không tải báo cáo', 'danger');
      } finally {
        this.loading = false;
      }
    },

    destroyChart() {
      if (this.chart) {
        this.chart.destroy();
        this.chart = null;
      }
    },

    renderChart() {
      this.destroyChart();
      const canvas = document.getElementById('report-donut');
      if (!canvas || !this.reportStats) return;

      const rows = (this.reportStats.by_category || [])
        .filter((c) => c.type === 'expense')
        .slice(0, 5);

      const css = getComputedStyle(document.documentElement);
      const colors = [1, 2, 3, 4, 5].map((i) =>
        css.getPropertyValue(`--color-chart-${i}`).trim()
      );

      this.chart = new Chart(canvas, {
        type: 'doughnut',
        data: {
          labels: rows.map((r) => r.name),
          datasets: [
            {
              data: rows.map((r) => r.total),
              backgroundColor: colors,
              borderWidth: 0,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'bottom',
              labels: { boxWidth: 12, font: { family: 'Manrope', size: 12 } },
            },
          },
        },
      });
    },

    async refreshFlatCategories() {
      try {
        const data = await api(`/categories?type=${this.form.type}&tree=1`);
        const flat = [];
        for (const root of data.items || []) {
          flat.push({ id: root.id, label: root.name });
          for (const child of root.children || []) {
            flat.push({ id: child.id, label: `${root.name} › ${child.name}` });
          }
        }
        this.flatCategories = flat;
      } catch {
        this.flatCategories = [];
      }
    },
  };
}

const root = document.getElementById('app');
root.innerHTML = uiHtml;

document.addEventListener('alpine:init', () => {
  Alpine.data('pfApp', createApp);
});

window.Alpine = Alpine;
Alpine.start();
